/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useState, useEffect, useContext,
} from 'react';
import { Plus, Mail } from 'lucide-react';
import EmailDeleteModal from './DeleteModal';
import EditModal from './EditModal';
import RowActionMenu from '../../../components/RowActionMenu';
import '../settings.css';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { UserContext } from '../../../contexts/User';
import { NotificationContext } from '../../../contexts/Notification';

const FREQUENCY = ['Daily', 'Weekly', 'Monthly'];

function ProductivityEmailSettings() {
  const {
    displayNotification,
  } = useContext(NotificationContext);
  const [emails, setEmails] = useState([]);
  const { activeWorkspace } = useContext(UserContext);

  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const getAllTeams = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAllTeamInfo(activeWorkspace.id),
      timeout: 60000,
    });

    if (response.success) {
      const mappedteams = response.data?.teams.map((team) => ({
        id: team.id,
        name: team.name,
      }));
      setTeams(mappedteams);
    }
  };

  const getAllMembers = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.members(activeWorkspace.id),
    });
    if (response.success) {
      const mappedmembers = response.data?.members.map((member) => ({
        id: member.id,
        name: member.user?.fullName,
      }));
      setUsers(mappedmembers);
    }
  };

  const getAllEmails = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getProductivityEmailSettings(activeWorkspace.id),
      timeout: 60000,
    });

    if (response.success) {
      const { productivityEmailSettings } = response.data;

      const updatedEmails = [];
      productivityEmailSettings.forEach((setting) => {
        const newEmail = {
          id: updatedEmails.length + 1,
          emailId: setting.id,
          isEnabled: setting.isEnabled,
          emailSubject: setting.emailSubject,
          sendFrequency: setting.sendFrequency,
          teams: setting.teams,
          recipients: setting.recipients,
        };

        updatedEmails.push(newEmail);
      });

      setEmails(updatedEmails);
    } else {
      displayNotification({
        message: 'An error occured while getting emails',
        severity: 'error',
      });
    }
  };

  const createEmail = async (email) => {
    const response = await sendRequest({
      method: 'post',
      url: API.WORKSPACE.setProductivityEmailSettings(activeWorkspace.id),
      data: { email },
    });

    if (response.success) {
      displayNotification({
        message: 'Create email successfully',
        severity: 'success',
      });
      getAllEmails();
    } else {
      displayNotification({
        message: 'An error occured while creating email',
        severity: 'error',
      });
    }
  };

  const deleteEmail = async (email) => {
    const response = await sendRequest({
      method: 'delete',
      url: API.WORKSPACE.deleteProductivityEmailSettings(activeWorkspace.id),
      data: { email },
    });

    if (response.success) {
      getAllEmails();
    }
  };

  const updateEmail = async (email) => {
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.updateProductivityEmailSettings(activeWorkspace.id),
      data: { email },
    });

    if (response.success) {
      getAllEmails();
    }
  };

  const handleToggle = (emailId) => {
    emails.forEach((email) => {
      if (email.id === emailId) {
        const updatedEmail = {
          emailId: email.emailId,
          isEnabled: !email.isEnabled,
          emailSubject: email.emailSubject,
          sendFrequency: email.sendFrequency,
          teams: email.teams,
          recipients: email.recipients,
        };
        updateEmail(updatedEmail);
      }
    });
  };

  const handleSaved = (emailName, assignedTeams, assignedUsers, frequency) => {
    const combinedRecipients = [];
    const combinedTeams = [];
    assignedUsers.forEach((item) => {
      combinedRecipients.push(users[users.findIndex((u) => u.name === item.name)].id);
    });
    assignedTeams.forEach((item) => {
      combinedTeams.push(teams[teams.findIndex((t) => t.name === item.name)].id);
    });
    const email = {
      isEnabled: 1,
      emailSubject: emailName,
      sendFrequency: frequency,
      recipients: combinedRecipients.join(','),
      teams: combinedTeams.join(','),
    };
    createEmail(email);
  };

  const handleUpdated = (emailName, assignedTeams, assignedUsers, frequency) => {
    const combinedRecipients = [];
    const combinedTeams = [];
    assignedUsers.forEach((item) => {
      combinedRecipients.push(users[users.findIndex((u) => u.name === item.name)].id);
    });
    assignedTeams.forEach((item) => {
      combinedTeams.push(teams[teams.findIndex((t) => t.name === item.name)].id);
    });
    const email = {
      emailId: selectedEmail.emailId,
      isEnabled: 1,
      emailSubject: emailName,
      sendFrequency: frequency,
      recipients: combinedRecipients.join(','),
      teams: combinedTeams.join(','),
    };
    updateEmail(email);
  };

  useEffect(() => {
    getAllTeams();
    getAllMembers();
    getAllEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countLabel = (csv, noun) => {
    const n = csv ? csv.split(',').filter(Boolean).length : 0;
    return `${n} ${noun}${n === 1 ? '' : 's'}`;
  };

  return (
    <>
      {showDeleteModal && (
        <EmailDeleteModal
          closeModal={() => setShowDeleteModal(false)}
          email={selectedEmail.emailSubject}
          onDelete={() => {
            deleteEmail({
              emailId: selectedEmail.emailId,
              isEnabled: selectedEmail.isEnabled,
              emailSubject: selectedEmail.emailSubject,
              sendFrequency: selectedEmail.sendFrequency,
              teams: selectedEmail.teams,
              recipients: selectedEmail.recipients,
            });
          }}
        />
      )}
      {showNewModal && (
        <EditModal
          closeModal={() => setShowNewModal(false)}
          teams={teams}
          users={users}
          selectedEmail={{ id: -1 }}
          onSaved={handleSaved}
        />
      )}
      {showEditModal && (
        <EditModal
          closeModal={() => setShowEditModal(false)}
          teams={teams}
          users={users}
          selectedEmail={selectedEmail}
          onSaved={handleUpdated}
        />
      )}

      <div className="px-4 py-4" style={{ fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
          <div className="min-w-0">
            <h4 className="text-base font-semibold text-ink-900 mb-0.5">Productivity Email Settings</h4>
            <p className="text-sm text-ink-500">Manage your workspace&apos;s automated emails.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-1.5 box-border h-10 px-4 rounded-[8px] text-sm font-semibold text-white bg-[#53A551] hover:bg-[#458a44] transition flex-shrink-0"
          >
            <Plus size={16} />
            New Email
          </button>
        </div>

        {/* Email list */}
        <div className="border border-rule rounded-[8px] divide-y divide-rule" style={{ borderStyle: 'solid' }}>
          {emails.length ? emails.map((email) => (
            <div key={email.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!email.isEnabled}
                  aria-label={`Toggle ${email.emailSubject}`}
                  onClick={() => handleToggle(email.id)}
                  className="relative inline-flex items-center rounded-full transition flex-shrink-0 border-0 p-0"
                  style={{ width: 40, height: 22, background: email.isEnabled ? '#3d5a8a' : '#c7cbd6', cursor: 'pointer' }}
                >
                  <span className="rounded-full bg-white" style={{ width: 18, height: 18, transform: email.isEnabled ? 'translateX(19px)' : 'translateX(3px)', transition: 'transform .2s ease', boxShadow: '0 1px 2px rgba(14,30,64,0.25)' }} />
                </button>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink-900 truncate">{email.emailSubject}</p>
                  <p className="text-xs text-ink-500">
                    {`${FREQUENCY[email.sendFrequency] || '—'} • ${countLabel(email.recipients, 'recipient')} • ${countLabel(email.teams, 'team')}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => { setSelectedEmail(email); setShowEditModal(true); }}
                  className="inline-flex items-center justify-center box-border h-9 px-3 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
                  style={{ borderStyle: 'solid' }}
                >
                  Edit
                </button>
                <RowActionMenu
                  ariaLabel={`Actions for ${email.emailSubject}`}
                  items={[
                    { label: 'Delete Email', destructive: true, onClick: () => { setSelectedEmail(email); setShowDeleteModal(true); } },
                  ]}
                />
              </div>
            </div>
          )) : (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <Mail size={28} className="text-ink-500" />
              <p className="text-sm text-ink-500">No automated emails yet.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ProductivityEmailSettings;
